import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Heart, ShoppingCart, X, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { useCartWishlist } from '@/context/CartWishlistContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Wishlist = () => {
  const navigate = useNavigate();
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notifyRegistrations, setNotifyRegistrations] = useState(new Set());
  const [productMedia, setProductMedia] = useState({});  // ← ADDED
  const { user, refreshCounts, updateWishlistCount, updateCartCount } = useCartWishlist();

  useEffect(() => {
    if (!user) {
      toast.error('Please login to view your wishlist');
      const redirectUrl = `${window.location.origin}/auth-callback`;
      window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/login`;
    } else {
      fetchWishlist();
      checkNotifyRegistrations();
    }
  }, [user]);

  // ← ADDED — same media fetching logic as Cart.jsx
  const fetchProductMedia = async (productId) => {
    if (productMedia[productId]) return;

    try {
      const response = await axios.get(`${API}/products/${productId}/media`);
      if (response.data.success && response.data.media.length > 0) {
        const mediaList = response.data.media;
        const primary = mediaList.find(media => media.is_primary && media.file_type === 'image');
        const images = mediaList.filter(media => media.file_type === 'image');

        setProductMedia(prev => ({
          ...prev,
          [productId]: primary || images[0]
        }));
      }
    } catch (error) {
      console.error('Failed to fetch product media:', error);
    }
  };

  const checkNotifyRegistrations = async () => {
    try {
      const response = await axios.get(`${API}/wishlist`, { withCredentials: true });
      const products = response.data.products || [];
      
      const registeredSet = new Set();
      for (const product of products) {
        if (product.stock <= 0) {
          const checkRes = await axios.get(`${API}/notifications/restock/check/${product.id}`, { withCredentials: true });
          if (checkRes.data.registered) {
            registeredSet.add(product.id);
          }
        }
      }
      setNotifyRegistrations(registeredSet);
    } catch (error) {
      console.error('Failed to check notifications:', error);
    }
  };

  const fetchWishlist = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/wishlist`, { withCredentials: true });
      const products = response.data.products || [];
      setWishlistItems(products);

      // ← ADDED — fetch media for all wishlist products
      products.forEach(product => {
        if (product.id) {
          fetchProductMedia(product.id);
        }
      });
    } catch (error) {
      console.error('Failed to fetch wishlist:', error);
      toast.error('Failed to load wishlist');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (productId) => {
    try {
      setWishlistItems(prev => prev.filter(item => item.id !== productId));
      updateWishlistCount(-1);

      await axios.delete(`${API}/wishlist/remove/${productId}`, { withCredentials: true });
      toast.success('Removed from wishlist');
    } catch (error) {
      console.error('Failed to remove from wishlist:', error);
      toast.error('Failed to remove item');
      fetchWishlist();
      refreshCounts();
    }
  };

  const handleMoveToCart = async (product) => {
    if (product.stock <= 0) {
      toast.error('Product is out of stock');
      return;
    }

    try {
      setWishlistItems(prev => prev.filter(item => item.id !== product.id));
      updateWishlistCount(-1);
      updateCartCount(1);

      await axios.post(`${API}/wishlist/move-to-cart/${product.id}`, {}, { withCredentials: true });
      toast.success('Moved to cart!');
    } catch (error) {
      console.error('Failed to move to cart:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to move to cart');
      }
      fetchWishlist();
      refreshCounts();
    }
  };

  const handleNotifyMe = async (productId) => {
    try {
      await axios.post(`${API}/notifications/restock?product_id=${productId}`, {}, { withCredentials: true });
      setNotifyRegistrations(prev => new Set([...prev, productId]));
      toast.success("You'll be notified when this item is back in stock!");
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to register for notifications');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black" data-testid="wishlist-loading">
        <Navbar />
        <div className="pt-24 px-4 sm:px-6 lg:px-8 pb-20 flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-red-600"></div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="wishlist-page">
      <Navbar />
      
      <div className="pt-24 px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl sm:text-5xl font-bold mb-4 flex items-center gap-3" data-testid="wishlist-title">
              <Heart className="text-red-500" size={40} />
              My <span className="accent-red">Wishlist</span>
            </h1>
            <p className="text-gray-400" data-testid="wishlist-description">
              {wishlistItems.length > 0 
                ? `You have ${wishlistItems.length} item${wishlistItems.length > 1 ? 's' : ''} in your wishlist`
                : 'Your wishlist is empty'}
            </p>
          </div>

          {/* Wishlist Items */}
          {wishlistItems.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="wishlist-grid">
              {wishlistItems.map(product => {
                const isOutOfStock = product.stock <= 0;
                // ← UPDATED — use media first, fallback to image_url (same pattern as Cart)
                const imageUrl = productMedia[product.id]?.file_url || product.image_url;

                return (
                  <div
                    key={product.id}
                    className={`product-card card-hover relative ${isOutOfStock ? 'opacity-60' : ''}`}
                    data-testid={`wishlist-item-${product.id}`}
                  >
                    {/* Remove Button */}
                    <button
                      onClick={() => handleRemove(product.id)}
                      className="absolute top-2 right-2 z-10 bg-red-600 hover:bg-red-700 text-white rounded-full p-2 transition-all hover:scale-110"
                      data-testid={`remove-btn-${product.id}`}
                      title="Remove from wishlist"
                    >
                      <X size={16} />
                    </button>

                    {/* Product Image */}
                    <div 
                      className="relative cursor-pointer"
                      onClick={() => navigate(`/product/${product.id}`)}
                    >
                      {/* ← UPDATED — imageUrl now uses media */}
                      <img 
                        src={imageUrl}
                        alt={product.name} 
                        className="w-full h-64 object-cover"
                      />
                      {isOutOfStock && (
                        <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                          <span className="text-white font-bold text-lg">OUT OF STOCK</span>
                        </div>
                      )}
                      {product.stock > 0 && product.stock < 5 && (
                        <div className="absolute top-4 left-4 bg-yellow-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                          Only {product.stock} left!
                        </div>
                      )}
                    </div>

                    {/* Product Details */}
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm text-red-500">{product.category}</p>
                        <p className="text-xs text-gray-400">{product.type}</p>
                      </div>
                      <h3 
                        className="text-lg font-semibold mb-2 line-clamp-1 cursor-pointer hover:text-red-500"
                        onClick={() => navigate(`/product/${product.id}`)}
                      >
                        {product.name}
                      </h3>
                      <p className="text-2xl font-bold accent-red mb-4">
                        ₹{product.price.toLocaleString()}
                      </p>

                      {/* Move to Cart / Notify Me Button */}
                      {isOutOfStock ? (
                        <button
                          onClick={() => handleNotifyMe(product.id)}
                          disabled={notifyRegistrations.has(product.id)}
                          className={`w-full flex items-center justify-center space-x-2 py-3 rounded-lg font-semibold transition-all ${
                            notifyRegistrations.has(product.id)
                              ? 'bg-green-600/50 text-white cursor-not-allowed'
                              : 'bg-blue-600 hover:bg-blue-700 text-white'
                          }`}
                          data-testid={`notify-me-btn-${product.id}`}
                        >
                          <Bell size={18} />
                          <span>{notifyRegistrations.has(product.id) ? 'Notification Set' : 'NOTIFY ME'}</span>
                        </button>
                      ) : (
                        <button
                          onClick={() => handleMoveToCart(product)}
                          className="w-full flex items-center justify-center space-x-2 py-3 rounded-lg font-semibold btn-primary hover:scale-105 transition-all"
                          data-testid={`move-to-cart-btn-${product.id}`}
                        >
                          <ShoppingCart size={18} />
                          <span>Move to Cart</span>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            // Empty State
            <div className="text-center py-20" data-testid="wishlist-empty">
              <div className="inline-block p-6 bg-white/5 rounded-full mb-6">
                <Heart size={64} className="text-gray-600" />
              </div>
              <h2 className="text-3xl font-bold mb-4">Your Wishlist is Empty</h2>
              <p className="text-gray-400 mb-8 max-w-md mx-auto">
                Start adding your favorite collectibles to your wishlist and never lose track of what you love!
              </p>
              <button
                onClick={() => navigate('/shop')}
                className="btn-primary inline-flex items-center space-x-2"
              >
                <span>Browse Shop</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Wishlist;